import axiosDefault from "./axios";
const IS_DEV = process.env.NODE_ENV === "development";

// A `message` value from this API isn't always a plain string — it can be
// another object nesting its own `.message` (observed: a downstream
// service's whole response, e.g. { message: "...", success: false },
// landed as the outer error's `message`). Drills down one `.message` at a
// time until it finds an actual string, capped so a malformed/circular
// shape can't loop forever. Returns undefined rather than a raw object if
// no string is ever found — callers must never hand a plain object to JSX
// as a child (React throws "Objects are not valid as a React child" —
// minified error #31 — which crashed the whole page, not just the wizard,
// the one time this went unhandled).
const extractMessageString = (value, depth = 0) => {
  if (typeof value === "string") {
    return value;
  }
  if (depth < 5 && value && typeof value === "object" && "message" in value) {
    return extractMessageString(value.message, depth + 1);
  }
  return undefined;
};

// The API's top-level `errors` field isn't consistently shaped: for
// field-level validation failures it's a flat array
// ([{property, message}]); for an unexpected/server error it can instead
// be its own wrapped error object (e.g.
// { code: "unexpected", message: "500 - null", errors: {} }, or with
// `message` itself nested a level deeper as above). Callers (e.g.
// EnterDataWizard) expect `error.errors` to always be an array they can
// safely check `.length`/`.map` on, and `error.message` to always be a
// plain string (or undefined) — without this normalization, a wrapped
// error object (or its nested message object) landed in `error.errors`/
// `error.message` as-is, which either silently failed every `.length`
// check downstream (error never rendered) or got handed to JSX as a raw
// object (hard crash).
const normalizeErrors = ({ code, message, errors }) => {
  const isWrappedErrorObject = !!errors && !Array.isArray(errors) && typeof errors === "object";
  const fieldErrors = Array.isArray(errors)
    ? errors
    : Array.isArray(errors?.errors)
      ? errors.errors
      : [];
  const resolvedMessage =
    extractMessageString(message) ??
    (isWrappedErrorObject ? extractMessageString(errors.message) : undefined);
  return {
    code: code || (isWrappedErrorObject ? errors.code : undefined),
    message: resolvedMessage,
    errors: fieldErrors,
  };
};

export const fetchBase = async ({ method = "get", ...options }, axios = axiosDefault) => {
  try {
    const response = await axios.request({ method, ...options });
    if (!response) {
      const message = "Unhandled Request Error";
      IS_DEV && console.error({ message: message + " Response Status is Null" });
      return { error: { message } };
    }
    const { data } = response;
    const { code, errors, message, ...restData } = data;
    // Some endpoints (e.g. dynamic/data) always include an `errors` key,
    // even on success, as an empty array — `!errors` treats that truthy-but-
    // empty array as a failure and mistakenly routes a valid response down
    // the error branch. Only an errors container that actually has entries
    // means the request failed.
    const hasErrors = Array.isArray(errors)
      ? errors.length > 0
      : !!errors && Object.keys(errors).length > 0;
    if (!hasErrors) {
      return { data };
    } else {
      const normalized = normalizeErrors({ code, message, errors });
      IS_DEV && console.error(normalized);
      return { data: restData, error: normalized };
    }
  } catch (exception) {
    const statusCode = exception?.response?.status;
    let message = IS_DEV ? exception.message || "Internal Error" : "Service Error";
    if (exception.response && exception.response.data) {
      let {error, code, errors, message } = exception.response.data;
      message = message || error?.message || error || "Service Error";
      const normalized = normalizeErrors({ code, message, errors });
      IS_DEV && console.error(normalized);
      return { error: { ...normalized, statusCode } };
    }
    if (IS_DEV) {
      if (exception.code !== "ERR_CANCELED") {
        console.error(exception);
      }
    }
    // `code` lets callers tell a client-side timeout ("ECONNABORTED") apart.
    return { error: { message, statusCode, code: exception.code } };
  }
};
